import React, { createContext, useState, useEffect, useCallback } from "react";

export const ThemeContext = createContext();

// 可用主题列表
export const THEMES = {
    dark: 'dark',      // 热带夜晚主题（默认）
    light: 'light',    // 亮色主题
};

// 从本地存储获取主题
const getStoredTheme = () => {
    try {
        const stored = localStorage.getItem('theme');
        if (stored && Object.values(THEMES).includes(stored)) {
            return stored;
        }
    } catch (e) {
        console.warn('无法访问 localStorage:', e);
    }
    // 默认使用暗色主题（热带夜晚）
    return THEMES.dark;
};

// 将主题保存到本地存储
const saveTheme = (theme) => {
    try {
        localStorage.setItem('theme', theme);
    } catch (e) {
        console.warn('无法保存主题到 localStorage:', e);
    }
};

const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(getStoredTheme);

    // 应用主题到 DOM
    const applyTheme = useCallback((newTheme) => {
        // 设置 data-theme 属性到 document.documentElement
        document.documentElement.setAttribute('data-theme', newTheme);

        // 同时设置 body 的 class 以便于样式选择器使用
        document.body.classList.remove('theme-dark', 'theme-light');
        document.body.classList.add(`theme-${newTheme}`);

        // 更新 meta theme-color 以便于移动端浏览器
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            metaThemeColor.setAttribute(
                'content',
                newTheme === 'dark' ? '#0f0c29' : '#ffffff'
            );
        }
    }, []);

    // 初始化时应用主题
    useEffect(() => {
        applyTheme(theme);
    }, [theme, applyTheme]);

    // 切换主题
    const toggleTheme = useCallback(() => {
        setTheme((prevTheme) => {
            const newTheme = prevTheme === THEMES.dark ? THEMES.light : THEMES.dark;
            saveTheme(newTheme);
            return newTheme;
        });
    }, []);

    // 设置特定主题
    const setSpecificTheme = useCallback((newTheme) => {
        if (Object.values(THEMES).includes(newTheme)) {
            setTheme(newTheme);
            saveTheme(newTheme);
        } else {
            console.warn(`无效的主题: ${newTheme}`);
        }
    }, []);

    // 检查是否为暗色主题
    const isDark = theme === THEMES.dark;

    return (
        <ThemeContext.Provider value={{
            theme,
            toggleTheme,
            setTheme: setSpecificTheme,
            isDark,
            themes: THEMES
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;